import { useCallback, useState } from 'react';
import { t } from 'i18next';

import { normalizeEmail } from '@/utils/validator';

import { requestPasswordReset, verifyPasswordResetCode, type PasswordResetChannel } from '../api';
import { toErrorMessage } from '../lib/errors';

export function usePasswordReset() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [email, setEmail] = useState('');

  const submit = useCallback(async (address: string, channel: PasswordResetChannel = 'EMAIL') => {
    setBusy(true);
    setError(null);
    try {
      const normalized = normalizeEmail(address);
      await requestPasswordReset(normalized, channel);
      setEmail(normalized);
      setSent(true);
    } catch (cause) {
      setError(toErrorMessage(cause, t('We could not send the reset link.')));
    } finally {
      setBusy(false);
    }
  }, []);

  const verifyCode = useCallback(async (code: string) => {
    setBusy(true);
    setError(null);
    try {
      const { recoveryToken } = await verifyPasswordResetCode(email, code);
      return recoveryToken;
    } catch (cause) {
      setError(toErrorMessage(cause, t('That code is not valid. Please try again.')));
      return null;
    } finally {
      setBusy(false);
    }
  }, [email]);

  const reset = useCallback(() => {
    setSent(false);
    setError(null);
    setEmail('');
  }, []);

  return { busy, error, sent, submit, verifyCode, reset };
}
